import { Checkbox } from "@mantine/core";
import { IconGps } from "@tabler/icons-react";
import { useEffect } from "react";
import { useController, useFormContext } from "react-hook-form";

import { useDispatch, useSelector } from "../../redux/hooks";
import { setFeature, setOowFeature } from "../../redux/slices/generatorSlice";
import { useLogbookRefsContext } from "../Logbook/LogbookRefsContext";
import useAmbAutomations from "./useAmbAutomations";
import useAmbPrevData from "../hooks/useAmbPrevData";
import { useShowContent } from "../hooks/useShowContent";

interface IExclusiveCheckbox {
  id: string;
  value: boolean;
  exclusive?: string;
  feature?: string;
  withClass?: string;
  tag: string | undefined;
  required?: boolean;
  ambGetPreviousData?: string;
}

const ExclusiveCheckbox = ({
  id,
  value,
  exclusive,
  feature,
  withClass,
  tag,
  required,
  ambGetPreviousData,
}: IExclusiveCheckbox) => {
  const extendedId = id + "-exclusive-" + exclusive;
  const refProps = useLogbookRefsContext(extendedId);
  const { control, getValues, setValue } = useFormContext();

  const dispatch = useDispatch();
  const { features } = useSelector(state => state.generatorSlice);
  const { handleShowContent } = useShowContent();
  const { field, fieldState } = useController({
    name: extendedId,
    control,
    defaultValue: value,
    rules: {
      validate: () => {
        if (required) {
          return Object.keys(getValues()).some(
            (key: string) =>
              key.includes("-exclusive-" + exclusive) && getValues(key),
          );
        }
        return true;
      },
    },
  });
  const handleAmbAutomations = useAmbAutomations({ tag, id: extendedId });
  useEffect(() => {
    if (feature?.startsWith("pms-")) {
      dispatch(
        setFeature({
          key: feature.replace("pms-", ""),
          feature: field.value,
        }),
      );
    }
  }, [feature, field]);

  // Handle initial showContent state on component mount
  useEffect(() => {
    if (feature === "showContent" && withClass) {
      handleShowContent(field.value, withClass);
    }
  }, [feature, withClass, field.value, handleShowContent]);

  const handleChange = (value: boolean) => {
    setValue(extendedId, value);
    void handleAmbAutomations(value);

    // Handle exclusive checkbox logic - uncheck other checkboxes in the same group
    for (const key in getValues()) {
      if (key.includes("-exclusive-" + exclusive) && key !== extendedId) {
        setValue(key, false);
        if (tag === "oowCheck" || features?.oowCheck?.[key] === true) {
          dispatch(
            setOowFeature({
              key,
              value: false,
            }),
          );
        }
      }
    }

    // Reset all showContent features to ensure clean state
    Object.keys(features).forEach(featureKey => {
      if (featureKey.startsWith("showContent_")) {
        const className = featureKey.replace("showContent_", "");
        handleShowContent(false, className);
      }
    });

    // Handle showContent feature for current checkbox (show content if checked)
    if (feature === "showContent" && withClass && value) {
      handleShowContent(true, withClass);
    }

    if (feature)
      dispatch(
        setFeature({
          key: feature.replace("pms-", ""),
          feature: value,
        }),
      );
  };
  useAmbPrevData({
    id: extendedId,
    dataTag: ambGetPreviousData,
    value: field.value,
    onChangeInput: handleChange,
  });
  return (
    <Checkbox
      style={{
        display: "flex",
        justifyContent: "center",
      }}
      w={"100%"}
      icon={IconGps}
      size={"lg"}
      key={id}
      {...field}
      error={fieldState.invalid}
      checked={field.value}
      onChange={e => handleChange(e.currentTarget.checked)}
      {...refProps}
    />
  );
};

export default ExclusiveCheckbox;
